/**
 * Is this operator taking calls, and the one request that changes it.
 *
 * Held by the rail rather than by the switch, for the reason `useCallDivert`
 * gives: the Settings section can be folded, and "your phone will not ring" is
 * not a fact that may disappear with a heading.
 *
 * The state lives on the server and the phone app can change it too, so it is
 * asked for again when the operator comes back to this window, and once more at
 * the moment a timer runs out. A row that keeps saying Offline after the phone
 * has started ringing again is worse than no row.
 */
import { useCallback, useEffect, useState } from "react";
import { useI18n } from "../../../i18n/I18nProvider";
import { getCallAvailability, setCallAvailability, type CallAvailability } from "../../../lib/callAvailability";
import { offlineUntil, type OfflineChoice } from "@wa/core/offline-until";
import { useToast } from "../../ui";

/** `setTimeout` overflows past this and fires at once. */
const MAX_TIMER_MS = 2_147_483_647;

export interface CallAvailabilityApi {
  /** Null until the state arrives, and after a request that failed. */
  state: CallAvailability | null;
  busy: boolean;
  goOnline: () => Promise<void>;
  goOffline: (choice: OfflineChoice) => Promise<void>;
}

/**
 * @param allowed This operator may work the telephone at all. False makes the
 *   whole thing inert rather than asking for a state the server would refuse.
 */
export function useCallAvailability(allowed: boolean): CallAvailabilityApi {
  const { t } = useI18n();
  const toast = useToast();
  const [state, setState] = useState<CallAvailability | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    getCallAvailability()
      .then(setState)
      // Silently, like the forwarding row beside it: the rail is not the place
      // to report that an optional section could not be read.
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!allowed) return;
    load();
    const onActive = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onActive);
    window.addEventListener("focus", onActive);
    return () => {
      document.removeEventListener("visibilitychange", onActive);
      window.removeEventListener("focus", onActive);
    };
  }, [allowed, load]);

  const until = state?.until ?? "";
  useEffect(() => {
    if (!until) return;
    const wait = new Date(until).getTime() - Date.now();
    if (Number.isNaN(wait)) return;
    const timer = window.setTimeout(load, Math.min(Math.max(wait, 0) + 1000, MAX_TIMER_MS));
    return () => window.clearTimeout(timer);
  }, [until, load]);

  async function write(online: boolean, moment: string | null, message: string) {
    setBusy(true);
    try {
      setState(await setCallAvailability(online, moment));
      toast(t(message));
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  return {
    state,
    busy,
    goOnline: () => write(true, null, "calls.online.toastOn"),
    goOffline: (choice) => write(false, offlineUntil(choice), "calls.online.toastOff"),
  };
}
